import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, uniqBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import { RootState } from '@share/utils';

import { getLuxuryHomeAvailability, ILuxuryHomeAvailability, ILuxuryHomeAvailabilityLoading } from '@store/slices';
import { CondoLocationsEnum, CondoRequestTypeEnum } from '@common-types';
import { useQueryParams } from '@utils';
import { ILuxuryHome, ILuxuryHomeLocation } from '@share/common-types';
import { Loading } from '@share/components';

import { LuxuryHomesPropertiesFilters } from '../../commons/property-filters/component';
import { LuxuryHomesDatesFilters } from '../../commons/dates-filters/component';
import { LuxuryHomesPropertyCard } from '../../commons/property-card/component';

import './style.scss';

export const LuxuryHomesProperties: React.FC = () => {
  
  const { location, checkIn, checkOut, guests } = useQueryParams();
  
  const { homes, locations } = useSelector((state: RootState) => state.luxuryHomesStore);

  const luxuryHomesSearchStore = useSelector((state: RootState) => state.luxuryHomesSearchStore);
  
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState<{ checkIn: string | null | undefined; checkOut: string | null | undefined; } | null>({ checkIn, checkOut });

  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedOcuppancy, setSelectedOcuppancy] = useState<string>('');
  const [selectedBedrooms, setSelectedBedrooms] = useState<string>('');
  const [selectedBathrooms, setSelectedBathrooms] = useState<string>('');
  const [search, setSearch] = useState('');

  const dispatch = useDispatch();

  const history = useHistory();

  const filterHomes = (homesToFilter: ILuxuryHome[]) => {
    return homesToFilter?.filter((home: ILuxuryHome) => (!selectedLocation || home?.location?.id?.toString() === selectedLocation?.toString()))
                         .filter((home: ILuxuryHome) => (!selectedOcuppancy || home?.units?.map((unit: any) => unit?.maxOccupancy).some((maxOccupancy: number) => (maxOccupancy || 0) >= (selectedOcuppancy ? Number(selectedOcuppancy) : 0))))
                         .filter((home: ILuxuryHome) => (!selectedBedrooms || home?.units?.map((unit: any) => unit?.bedroomsCount).some((bedroomsCount: number) => (bedroomsCount || 0) >= (selectedBedrooms ? Number(selectedBedrooms) : 0))))
                         .filter((home: ILuxuryHome) => (!selectedBathrooms || home?.units?.map((unit: any) => unit?.bathroomsCount).some((bathroomsCount: number) => (bathroomsCount || 0) >= (selectedBathrooms ? Number(selectedBathrooms) : 0))))
                         .filter((home: ILuxuryHome) => (isEmpty(search) || home?.title?.toLowerCase().includes(search.toLowerCase())));
  }


  const initialHomes = useMemo(() => {
    const locationId = selectedLocation ? selectedLocation : location;
    return locationId ? homes?.filter((l: ILuxuryHome) => l.location.id?.toString() === locationId?.toString()) : homes;  
  }, [homes, selectedLocation]);


  const initialHomesUniqIds = useMemo(() => {
    return uniqBy(initialHomes, 'id');
  }, [initialHomes]);
  
  const homesfilteredService = useMemo(() => {
    if (dates?.checkIn && dates?.checkOut) {
      const roomIds = luxuryHomesSearchStore.homes?.map((h: ILuxuryHomeAvailability) => h.home?.units?.map((u: any) => ({ homeId: h.home.id, roomId: u.roomId }))).flat();
      const initialHomesFiltered = homes.filter((h: any) => roomIds.map((r: any) => r.homeId).includes(h.id))
                                        .filter((h: any) => h.units?.some((u: any) => roomIds.map((r: any) => r.roomId).includes(u.id)));
      
      return initialHomesFiltered;
    } else {
      return initialHomes;
    }
  }, [luxuryHomesSearchStore.homes]);

  const homesfilteredFinal = filterHomes(homesfilteredService);

  useEffect(() => {
    if (locations?.length && location) {
      const selected = locations.find((l: ILuxuryHomeLocation) => l.id === Number(location));      
      if (selected) {
        setSelectedLocation(selected.id);
      }
    }

    if (guests) {
      setSelectedOcuppancy(guests);
    }
    
  }, [history.location.search, locations]);

  useEffect(() => {
    refreshLuxuryHomeAvailability(dates);
  }, [initialHomesUniqIds]);

  useEffect(() => {
    setLoading(luxuryHomesSearchStore.loading.some((l: ILuxuryHomeAvailabilityLoading) => l.loading));
  }, [luxuryHomesSearchStore.loading]);
  
  const refreshLuxuryHomeAvailability = (dates: any) => {
    if (dates?.checkIn && dates?.checkOut && initialHomes?.length) {
      initialHomesUniqIds.map((h: any) =>
        dispatch(getLuxuryHomeAvailability(h?.id, {
          requestType: CondoRequestTypeEnum.Strict,
          adultsCount: 2,
          childrenCount: 0,
          bedroomsCount: 0,
          checkIn: dates?.checkIn ? dates?.checkIn : '',
          checkOut: dates?.checkOut ? dates?.checkOut : '',
          includeStudio: true,
          location: {
            geoLocation: {
              latitude  : h?.location?.location?.lat,
              longitude: h?.location?.location?.lng,
            },
            id: h?.location?.id?.toString(),
            locationType: CondoLocationsEnum.GenericCondo,
            name: h?.location?.name,
          }
        })));
    } else {
    }
  }

  return (
    <section className="luxury-place">
      <LuxuryHomesPropertiesFilters 
        selectedLocation={selectedLocation}
        selectedOcuppancy={selectedOcuppancy}
        selectedBedrooms={selectedBedrooms}
        selectedBathrooms={selectedBathrooms}
        search={search}

        disabledLocations={loading}
        disabledSearch={loading}
        disabledOcuppancy={loading}
        disabledBedrooms={loading}
        disabledBathrooms={loading}

        setSelectedLocation={setSelectedLocation}
        setSelectedOcuppancy={setSelectedOcuppancy}
        setSelectedBedrooms={setSelectedBedrooms}
        setSelectedBathrooms={setSelectedBathrooms}
        setSearch={setSearch}
      />

      <LuxuryHomesDatesFilters 
        dates={dates} 
        disabled={loading}
        onDatesChange={d => {
          setDates(d);
          refreshLuxuryHomeAvailability(d);
        }}
      />

      {loading && (
        <div className="luxury-place__properties" style={{ minHeight: 350 }}>
          <Loading />
        </div>)}

      {(!loading && !homesfilteredFinal?.length) && (
        <div className="luxury-place__properties one-line">
          <div className="luxury-place__properties-empty">
            <h2>No properties found for the selected criteria</h2>
          </div>
        </div>)}

      {(!loading && !!homesfilteredFinal?.length) && (
        <div className="luxury-place__properties">
          {homesfilteredFinal?.map((home: ILuxuryHome, index: number) => (
            <LuxuryHomesPropertyCard
              key={`${index}-${dates?.checkIn || 'NA'}-${dates?.checkOut || 'NA'}`}
              home={home}

              locationId={location}
              dates={dates}
              guests={2}

              isList
            />))}
        </div>)}
    </section>
  );
};
